// src/pages/ChoreographiesList.js - all choreographies of the leader's group.
//
// Audit fixes: FL-13 (StatusBadge), FL-19 (readable categories), FL-07 (retry), FL-22
// (grouped by competition, per-card checklist, "Zaregistrovat vše" for pre-registrations).
// Requirement 2.1: season tabs (components/leader/SeasonTabs.js: the current season is always
// offered); inside the season the newest competition first (Grand Finále days above the semifinals).
// A competition with drafts shows its online presence deadline (a week before it); after a
// partly failed "Zaregistrovat vše" the dialog keeps only the entries that failed.
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, CalendarDays, CheckCircle2, Circle, Music2, Plus, Users } from 'lucide-react';
import useFetch from '../hooks/useFetch';
import { choreographiesApi, getErrorMessage } from '../services/api';
import { categoryLabel } from '../constants/categories';
import { formatDateLong, countLabel, isPastCompetition, isRegistrationClosed, presenceDeadline } from '../utils/format';
import { Alert, Button, ConfirmDialog, EmptyState, PageHeader, Spinner, StatusBadge, useToast } from '../components/ui';
import LoadError from '../components/leader/LoadError';
import { DeadlineText, RegistrationBadge } from '../components/leader/CompetitionBadges';
import SeasonTabs, { useSeasonTabs } from '../components/leader/SeasonTabs';

const CHOREO_FORMS = ['choreografie', 'choreografie', 'choreografií'];

function Check({ done, children }) {
  const Icon = done ? CheckCircle2 : Circle;
  return (
    <span className={`inline-flex items-center gap-1 ${done ? 'text-green-700' : 'text-amber-800'}`}>
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </span>
  );
}

function nextStepText(choreography) {
  if (isPastCompetition(choreography.competition)) return null; // nothing to do any more
  if (choreography.status === 'pre_registered') {
    if (isRegistrationClosed(choreography.competition)) return null; // too late to register
    return choreography.canRegister ? 'Registrace je otevřená – zaregistrujte' : 'Čeká na otevření registrace';
  }
  if (choreography.status === 'draft') {
    return choreography.musicUploaded ? 'Potvrďte prezenci' : 'Nahrajte hudbu a potvrďte prezenci';
  }
  return null;
}

function ChoreographyCard({ choreography }) {
  const next = nextStepText(choreography);
  const count = choreography.dancersCount ?? 0;
  const confirmed = ['confirmed', 'approved'].includes(choreography.status);
  return (
    <Link
      to={`/choreographies/${choreography.id}`}
      className="block rounded-lg bg-white p-4 shadow transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:p-5"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{choreography.name}</h3>
          <p className="mt-0.5 text-sm text-gray-600">
            {categoryLabel(choreography.discipline, choreography.ageCategory, choreography.league)}
          </p>
        </div>
        <StatusBadge kind="choreography" status={choreography.status} className="self-start" />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <Check done={count >= 4}>
          <Users className="h-4 w-4" aria-hidden="true" />
          {countLabel(count, ['tanečník', 'tanečníci', 'tanečníků'])}
        </Check>
        <Check done={Boolean(choreography.musicUploaded)}>
          <Music2 className="h-4 w-4" aria-hidden="true" />
          {choreography.musicUploaded ? 'Hudba nahraná' : 'Chybí hudba'}
        </Check>
        {choreography.status !== 'rejected' && (
          <Check done={confirmed}>{confirmed ? 'Prezence potvrzena' : 'Prezence nepotvrzena'}</Check>
        )}
      </div>
      {next && <p className="mt-2 text-sm font-medium text-indigo-700">Další krok: {next} →</p>}
    </Link>
  );
}

const yearOf = (choreography) => Number(String(choreography.competition?.date || '').slice(0, 4)) || null;

const ChoreographiesList = () => {
  const toast = useToast();
  const { data, error, isLoading, reload } = useFetch(
    () => choreographiesApi.getAll().then((res) => res.data || []),
    'all'
  );
  const [bulk, setBulk] = useState(null); // { competition, items }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);

  // Season tabs (2.1): the current season is always offered, see SeasonTabs.js.
  const { tabs, selected: selectedYear, select: selectYear, currentSeason } = useSeasonTabs(data, yearOf);

  // Grouped by competition; the API returns the newest competitions first.
  const groups = useMemo(() => {
    const map = new Map();
    (data || []).filter((c) => yearOf(c) === selectedYear).forEach((c) => {
      const key = c.competition?.id ?? 'none';
      if (!map.has(key)) map.set(key, { competition: c.competition || {}, items: [] });
      map.get(key).items.push(c);
    });
    return Array.from(map.values());
  }, [data, selectedYear]);

  const closeBulk = useCallback(() => {
    setBulk(null);
    setBulkError(null);
  }, []);

  const registerAll = async () => {
    if (!bulk) return;
    setBulkBusy(true);
    setBulkError(null);
    const failures = [];
    const failedItems = [];
    let done = 0;
    // One after another: the registration order is kept exactly as listed.
    for (const item of bulk.items) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await choreographiesApi.register(item.id);
        done += 1;
      } catch (err) {
        failures.push(`${item.name}: ${getErrorMessage(err)}`);
        failedItems.push(item);
      }
    }
    setBulkBusy(false);
    if (failures.length) {
      // Only what failed stays in the dialog, so "Zaregistrovat" retries just those.
      setBulk((current) => (current ? { ...current, items: failedItems } : current));
      setBulkError(
        <>
          <p>
            {done > 0 ? `Zaregistrováno ${countLabel(done, CHOREO_FORMS)}. ` : ''}Tyto se nepodařilo zaregistrovat:
          </p>
          <ul className="mt-1 list-disc pl-5">
            {failures.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </>
      );
    } else {
      setBulk(null);
      toast.success(`Zaregistrováno ${countLabel(done, CHOREO_FORMS)}. Teď nahrajte hudbu a potvrďte prezenci.`);
    }
    reload({ silent: true });
  };

  if (isLoading && !data) return <Spinner label="Načítám choreografie…" />;
  if (error && !data) return <LoadError title="Choreografie se nepodařilo načíst" error={error} onRetry={reload} />;

  const renderGroup = (group) => {
    const { competition } = group;
    const registrable = isRegistrationClosed(competition) ? [] : group.items.filter((c) => c.canRegister);
    const presenceDue =
      !isPastCompetition(competition) && group.items.some((c) => c.status === 'draft') ? presenceDeadline(competition) : null;
    return (
      <section key={competition.id ?? 'none'} className="space-y-3" aria-label={competition.name}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {competition.id ? (
                <Link to={`/competitions/${competition.id}`} className="hover:text-indigo-700">
                  {competition.name}
                </Link>
              ) : (
                competition.name || 'Bez soutěže'
              )}
            </h2>
            <p className="flex items-center gap-1.5 text-sm text-gray-600">
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
              {formatDateLong(competition.date)}
              <span aria-hidden="true">·</span>
              {countLabel(group.items.length, CHOREO_FORMS)}
            </p>
            {presenceDue && <DeadlineText date={presenceDue} label="Online prezence do" className="mt-1 text-sm" />}
          </div>
          <RegistrationBadge competition={competition} />
        </div>
        {registrable.length > 0 && (
          <Alert tone="info" title="Registrace je otevřená">
            <p>
              Máte zde {countLabel(registrable.length, ['předregistraci', 'předregistrace', 'předregistrací'])}. Zaregistrujte
              je, teprve pak se počítají do soutěže.
            </p>
            <Button className="mt-2" onClick={() => setBulk({ competition, items: registrable })}>
              Zaregistrovat vše ({registrable.length})
            </Button>
          </Alert>
        )}
        <div className="grid grid-cols-1 gap-3">
          {group.items.map((c) => (
            <ChoreographyCard key={c.id} choreography={c} />
          ))}
        </div>
      </section>
    );
  };

  return (
    <div>
      <PageHeader
        title="Choreografie"
        subtitle="Přehled přihlášených choreografií podle soutěží."
        actions={
          <Link
            to="/competitions"
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nová přihláška
          </Link>
        }
      />

      {(data || []).length === 0 ? (
        <EmptyState
          icon={Music2}
          title="Zatím nemáte žádné choreografie"
          action={
            <Link
              to="/competitions"
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <Award className="h-4 w-4" aria-hidden="true" />
              Vybrat soutěž
            </Link>
          }
        >
          Choreografii přihlásíte v detailu soutěže – vyberte kategorii a klikněte na „Přihlásit“.
        </EmptyState>
      ) : (
        <div className="space-y-6">
          <SeasonTabs tabs={tabs} selected={selectedYear} onSelect={selectYear} />
          {groups.length === 0 ? (
            <EmptyState
              icon={Music2}
              title={`V sezóně ${selectedYear} zatím nemáte žádné choreografie`}
              action={
                selectedYear >= (currentSeason || selectedYear) && (
                  <Link
                    to="/competitions"
                    className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    <Award className="h-4 w-4" aria-hidden="true" />
                    Vybrat soutěž
                  </Link>
                )
              }
            >
              Choreografii přihlásíte v detailu soutěže – vyberte kategorii a klikněte na „Přihlásit“ (před otevřením
              registrace „Předregistrovat“). Starší sezóny najdete v záložkách výše.
            </EmptyState>
          ) : (
            <div className="space-y-8">{groups.map(renderGroup)}</div>
          )}
        </div>
      )}

      {bulk && (
        <ConfirmDialog
          title={`Zaregistrovat do soutěže ${bulk.competition.name}?`}
          confirmLabel={`Zaregistrovat (${bulk.items.length})`}
          loading={bulkBusy}
          onConfirm={registerAll}
          onCancel={closeBulk}
        >
          <p>Zaregistrují se tyto předregistrace a započítají se do kapacity soutěže:</p>
          <ul className="list-disc pl-5">
            {bulk.items.map((c) => (
              <li key={c.id}>{c.name}</li>
            ))}
          </ul>
          {bulkError && <Alert tone="error">{bulkError}</Alert>}
        </ConfirmDialog>
      )}
    </div>
  );
};

export default ChoreographiesList;
